import React from "react";
import { Alert, Box, Grid, Typography } from "@mui/material";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

import MonitoringHeader from "../components/MonitoringHeader";
import StatCard from "../components/StatCard";
import DeviceTelemetryCard from "../components/DeviceTelemetryCard";
import DripSessionListTable from "../components/DripSessionTable";
import useAutoLiveSession from "../hooks/useLiveSessionManager";

export default function LiveMonitoringPage() {
  const {
    telemetryRows,
    sessionRows,
    loading,
    error,
    isLiveConnected,
    currentSessionId,
    lastSeenTime,
    refreshAll,
  } = useAutoLiveSession();

  const latestTelemetry = telemetryRows?.[0] || null;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", px: 2 }}>
        <MonitoringHeader
          title="Live Monitoring"
          subtitle="Session auto starts when device data comes and auto completes if data does not come for 20 seconds."
          onRefresh={refreshAll}
          loading={loading}
        />

        <Alert
          severity={isLiveConnected ? "success" : "warning"}
          sx={{ mb: 3, borderRadius: 3 }}
        >
          {isLiveConnected
            ? `Live device data detected. Active session: ${currentSessionId || "Creating..."}`
            : "No live device data detected. If no data comes for 20 seconds, session will be completed."}
        </Alert>

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Live Status"
              value={isLiveConnected ? "Connected" : "Disconnected"}
              subtitle="Telemetry data stream state"
              icon={<MemoryRoundedIcon sx={{ color: "#4f46e5" }} />}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Current Session"
              value={currentSessionId || "—"}
              subtitle="Auto-created live drip session"
              icon={<PlaylistAddCheckRoundedIcon sx={{ color: "#0f766e" }} />}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Last Data Time"
              value={lastSeenTime || "—"}
              subtitle="Latest telemetry arrival time"
              icon={<AccessTimeRoundedIcon sx={{ color: "#c2410c" }} />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              Live Device Data
            </Typography>
            <DeviceTelemetryCard item={latestTelemetry} />
          </Grid>

          <Grid item xs={12}>
            <DripSessionListTable rows={sessionRows} loading={loading} error={error} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}